import React, { FC, useCallback, useContext, useState } from 'react'
import { Grid } from '@mui/material'
import { FormattedMessage, useIntl } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import styles from '../../../styles'
import { Form } from 'react-final-form'
import Button from '../../../components/button/Button'
import { buttonSize } from '../../../components/button/enums/buttonEnum'
import ContactForm from './ContactForm'
import { prospectTypeEnum } from '../../../enums/prospectTypeEnum'
import { ERREUR_PROSPECT_DEJA_EXISTANT, INITIAL_CREATION_CONTACT, REQUIRED_ACCOUNT_FIELDS, REQUIRED_CONTACT_FIELDS } from '../utils/creationContactConstants'
import AccountForm from './AccountForm'
import { isEmailOk } from '../../../utils/formUtils'
import { CreationContactFormValues } from '../types/contactModels'
import { ContactFormValuesEnum } from '../types/contactFormValuesEnum'
import ProspectTypeField from './ProspectTypeField'
import { createProspect } from '../api/creationContactApi'
import Loader from '../../../components/loader/Loader'
import { WHITE } from '../../../theme'
import { SelectionPaneContext } from '../../../components/selectionPane/components/SelectionPaneContext'
import { isEmpty } from 'lodash'
import links from '../../../enums/linksEnum'
import { useNavigate } from 'react-router'
import ErrorCard from '../../../components/errorCard/ErrorCard'

const FormCreationContact: FC = () => {
	const intl = useIntl()
	const navigate = useNavigate()
	const { classes } = useStyles()
	const [creationLoading, setCreationLoading] = useState<boolean>(false)
	const { updateCurrentClient, hasCurrentOffer } = useContext(SelectionPaneContext)
	const [errorMessage, setErrorMessage] = useState<string>()

	const onSubmit = useCallback((values: CreationContactFormValues, form: any) => {
		setErrorMessage(undefined)
		const requiredFields = values[ContactFormValuesEnum.PROSPECT_TYPE] === prospectTypeEnum.CONTACT ? REQUIRED_CONTACT_FIELDS : REQUIRED_ACCOUNT_FIELDS

		// Filtre les valeurs nécessaires pour le type de prospect
		const userData = Object.keys(values)
			.filter(key => requiredFields.includes(key))
			.reduce((obj, key) => ({
				...obj,
				[key]: values[key as keyof typeof values]
			}), {})

		setCreationLoading(true)
		createProspect(userData)
			.then((client) => {
				if (!isEmpty(client)) {
					// On mets à jour le client courant
					updateCurrentClient(client)
					// S'il y a une offre courante, on affiche la page de planification
					// Sinon, on affiche la page de recherche d'offre
					hasCurrentOffer ? navigate(links.PLANIFICATION_VISITE) : navigate(links.RECHERCHE_OFFRE)
				}
			})
			.catch((e) => {
				if (e === ERREUR_PROSPECT_DEJA_EXISTANT) {
					setErrorMessage(intl.formatMessage({ id: 'errors.dejaExistant' }))
				}
			})
			.finally(() => setCreationLoading(false))
	}, [hasCurrentOffer, navigate, updateCurrentClient, intl])

	const validate = (values: CreationContactFormValues) => {
		const errors: any = {}
		const requiredFields = values[ContactFormValuesEnum.PROSPECT_TYPE] === prospectTypeEnum.CONTACT ? REQUIRED_CONTACT_FIELDS : REQUIRED_ACCOUNT_FIELDS
		requiredFields.forEach((field) => {
			if (!values[field as keyof typeof values]) {
				errors[field] = intl.formatMessage({
					id: 'errors.required',
					defaultMessage: 'Requis*',
					description: 'Required message'
				})
			}
		})
		if (!!values[ContactFormValuesEnum.EMAIL] && !isEmailOk(values[ContactFormValuesEnum.EMAIL])) {
			errors[ContactFormValuesEnum.EMAIL] = intl.formatMessage({
				id: 'errors.formatEmail',
				defaultMessage: 'Format du courriel incorrect',
				description: 'Format email error'
			})
		}
		return errors
	}

	return (
		<Grid container item className={classes.creationContactContainer}>
			<Form
				initialValues={INITIAL_CREATION_CONTACT}
				onSubmit={onSubmit}
				validate={validate}
				render={({ handleSubmit, values }) => (
					<form onSubmit={handleSubmit}>
						<Grid container item spacing="40px" className={classes.formContainer}>
							{!!errorMessage && <Grid container item> <ErrorCard message={errorMessage} /></Grid>}
							<Grid container item>
								<ProspectTypeField />
							</Grid>
							{values[ContactFormValuesEnum.PROSPECT_TYPE] === prospectTypeEnum.CONTACT ? <ContactForm /> : <AccountForm />}
							<Grid container item>
								<Button
									background="backgroundAccessibility"
									color="colorWhite"
									textStyle="text4"
									size={buttonSize.small}
									buttonStyle={classes.buttonStyle}
									fullWidth
									disabled={creationLoading}
								>
									{creationLoading ? <Loader size={20} /> :
										<FormattedMessage
											id="creationContact.btnCreation"
											defaultMessage="Créer le contact"
											description="Bouton pour créer le contact"
										/>}
								</Button>
							</Grid>
						</Grid>
					</form>
				)}
			/>
		</Grid>
	)
}

const useStyles = makeStyles()((theme) => ({
	...styles(theme),
	creationContactContainer: {
		padding: '0 20px'
	},
	buttonStyle: {
		maxWidth: 215,
		borderRadius: 40,
		'& svg': {
			color: WHITE
		}
	},
	formContainer: {
		maxWidth: 300
	}
}))

export default FormCreationContact